import { useState, useRef } from 'react';
import { Database, Download, Upload, Clock, FileText, CheckCircle, AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface BackupRecord {
  id: string;
  created_at: string;
  backup_size: number;
  backup_type: string;
  status: string;
  tables_included: string[];
  created_by: string;
}

const BackupManager = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreMode, setRestoreMode] = useState<'merge' | 'replace'>('merge');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch existing backups
  const { data: backups, isLoading } = useQuery({
    queryKey: ['system-backups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('schedule_backups')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      
      // Transform the data to match our interface
      return (data || []).map(backup => ({
        id: backup.id,
        created_at: backup.backup_date, // Use backup_date from schedule_backups table
        backup_size: 0, // Will be populated by the backup function
        backup_type: 'complete',
        status: 'completed',
        tables_included: [],
        created_by: backup.created_by || ''
      })) as BackupRecord[];
    },
    refetchInterval: 30000,
  });

  // Create backup mutation
  const createBackupMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('create-complete-backup', {
        body: { 
          backup_type: 'complete',
          include_tables: [
            'profiles', 'teams', 'emergency_reports', 'team_schedules', 
            'applications', 'missions', 'leave_requests', 'guard_equipment',
            'notifications', 'audit_logs', 'webhook_configs'
          ]
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Backup Created',
        description: 'Complete system backup has been created successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['system-backups'] });
      setIsCreating(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Backup Failed',
        description: error.message || 'Failed to create backup.',
        variant: 'destructive',
      });
      setIsCreating(false);
    },
  });

  const handleCreateBackup = async () => {
    setIsCreating(true);
    createBackupMutation.mutate();
  };

  const downloadBackup = async (backupId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('download-backup', {
        body: { backup_id: backupId }
      });

      if (error) throw error;

      // Create download link
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-${backupId}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Download Started',
        description: 'Backup file download has started.',
      });
    } catch (error: any) {
      toast({
        title: 'Download Failed',
        description: error.message || 'Failed to download backup.',
        variant: 'destructive',
      });
    }
  };

  // Restore backup mutation
  const restoreBackupMutation = useMutation({
    mutationFn: async (backupData: any) => {
      const { data, error } = await supabase.functions.invoke('restore-backup', {
        body: { 
          backup_data: backupData,
          restore_mode: restoreMode
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: 'Restore Completed',
        description: data.message || 'Backup has been restored successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['system-backups'] });
      setIsRestoring(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Restore Failed',
        description: error.message || 'Failed to restore backup.',
        variant: 'destructive',
      });
      setIsRestoring(false);
    },
  });

  const handleFileRestore = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid File',
        description: 'Please select a valid JSON backup file.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsRestoring(true);
      const fileContent = await file.text();
      const backupData = JSON.parse(fileContent);

      // Validate backup data structure
      if (!backupData.backup_metadata || !backupData.tables) {
        throw new Error('Invalid backup file format');
      }

      restoreBackupMutation.mutate(backupData);
    } catch (error: any) {
      toast({
        title: 'File Error',
        description: error.message || 'Failed to read backup file.',
        variant: 'destructive',
      });
      setIsRestoring(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'in_progress':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />In Progress</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">System Backup Management</h3>
          <p className="text-muted-foreground">
            Create and manage complete system backups
          </p>
        </div>
        <Button 
          onClick={handleCreateBackup}
          disabled={isCreating || createBackupMutation.isPending}
          size="lg"
        >
          <Database className="w-4 h-4 mr-2" />
          {isCreating ? 'Creating...' : 'Create Complete Backup'}
        </Button>
      </div>

      {/* Backup Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <Database className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm font-medium">Total Backups</p>
              <p className="text-2xl font-bold">{backups?.length || 0}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm font-medium">Last Backup</p>
              <p className="text-sm">
                {backups?.[0]?.created_at 
                  ? new Date(backups[0].created_at).toLocaleDateString()
                  : 'Never'
                }
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-sm font-medium">Total Size</p>
              <p className="text-sm">
                {formatFileSize(backups?.reduce((sum, backup) => sum + (backup.backup_size || 0), 0) || 0)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Restore Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-md font-semibold">Restore from Backup</h4>
            <p className="text-sm text-muted-foreground">
              Upload and restore a system backup file (includes schedules)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={restoreMode} onValueChange={(value: 'merge' | 'replace') => setRestoreMode(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="merge">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" />
                    Merge
                  </div>
                </SelectItem>
                <SelectItem value="replace">
                  <div className="flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" />
                    Replace
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={handleFileRestore}
              disabled={isRestoring || restoreBackupMutation.isPending}
              variant="outline"
            >
              <Upload className="w-4 h-4 mr-2" />
              {isRestoring ? 'Restoring...' : 'Select Backup File'}
            </Button>
          </div>
        </div>

        <div className="bg-muted/50 rounded-md p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-sm font-medium">Restore Mode Information:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li><strong>Merge:</strong> Updates existing records and adds new ones (recommended)</li>
                <li><strong>Replace:</strong> Completely replaces all data (destructive)</li>
              </ul>
              <p className="text-xs text-muted-foreground">
                ⚠️ Always create a backup before restoring. This operation cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".json"
          style={{ display: 'none' }}
        />
      </Card>

      {/* Backup History */}
      <Card className="p-6">
        <h4 className="text-md font-semibold mb-4">Backup History</h4>
        
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse"></div>
            ))}
          </div>
        ) : backups && backups.length > 0 ? (
          <div className="space-y-3">
            {backups.map((backup) => (
              <div key={backup.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <Database className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="font-medium">
                      Complete System Backup
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Created on {new Date(backup.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Size: {formatFileSize(backup.backup_size || 0)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {getStatusBadge(backup.status)}
                  {backup.status === 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadBackup(backup.id)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No backups found. Create your first backup to get started.</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BackupManager;